import { useCallback, useEffect, useRef, useState } from 'react';
import {
  confirmMultiPackageOutput,
  getIncompleteMultiPackageRecoveryHistory,
  retryMultiPackagePrint,
} from '../features/multiPackagePrint/api';
import { createMultiPackageZip, downloadArtifact } from '../features/multiPackagePrint/multiPackageZip';
import type { MultiPackagePrintResult } from '../types/multiPackagePrint';
import { getErrorMessage } from '../utils/requestErrors';

export default function useMultiPackageRecoveryHistory(
  organizationId: number | null | undefined,
  enabled: boolean,
  canResume: boolean,
) {
  const identity = `${organizationId}:${enabled}:${canResume}`;
  const scopeRef = useRef({ identity });
  if (scopeRef.current.identity !== identity) scopeRef.current = { identity };
  const [state, setState] = useState<{
    scope: typeof scopeRef.current;
    batches: MultiPackagePrintResult[];
    loading: boolean;
    error: boolean;
    actionError: string | null;
  }>({ scope: scopeRef.current, batches: [], loading: false, error: false, actionError: null });
  const requestIdRef = useRef(0);
  const mutationRef = useRef<symbol | null>(null);

  const refresh = useCallback(async () => {
    const scope = scopeRef.current;
    const requestId = ++requestIdRef.current;
    const current = () => scope === scopeRef.current && requestId === requestIdRef.current;
    setState({ scope, batches: [], loading: enabled && !!organizationId, error: false, actionError: null });
    if (!enabled || !organizationId) return;
    try {
      const batches = await getIncompleteMultiPackageRecoveryHistory(organizationId);
      if (current()) setState({ scope, batches, loading: false, error: false, actionError: null });
    } catch {
      if (current()) setState({ scope, batches: [], loading: false, error: true, actionError: null });
    }
  }, [organizationId, enabled]);

  useEffect(() => {
    mutationRef.current = null;
    void refresh();
    return () => {
      requestIdRef.current += 1;
      scopeRef.current = { identity: scopeRef.current.identity };
    };
  }, [refresh, canResume]);

  const recover = async (batchId: string, batch?: MultiPackagePrintResult) => {
    if (!enabled || !organizationId || !canResume || mutationRef.current) return;
    if (batch && !batch.outputManifest.items.length) return;
    const scope = scopeRef.current;
    const requestId = ++requestIdRef.current;
    const lease = Symbol('multi-package-recovery');
    mutationRef.current = lease;
    const current = () => scope === scopeRef.current && requestId === requestIdRef.current;
    setState((previous) => ({ ...previous, scope, loading: true, error: false, actionError: null }));
    try {
      if (batch) {
        const artifact = await createMultiPackageZip(organizationId, batch);
        if (!current()) return;
        downloadArtifact(artifact);
        const pending = batch.items.filter((item) => item.stage === 'OutputPending'
          && batch.outputManifest.items.some((entry) => entry.sequence === item.sequence));
        if (pending.length) await confirmMultiPackageOutput(organizationId, batchId,
          pending.map((item) => item.sequence));
      } else {
        await retryMultiPackagePrint(organizationId, batchId);
      }
      if (current()) await refresh();
    } catch (error) {
      if (current()) setState((previous) => ({ ...previous, loading: false,
        actionError: getErrorMessage(error, batch
          ? 'The package ZIP could not be downloaded. Try again.'
          : 'The batch could not be resumed. Try again.'),
      }));
    } finally {
      if (mutationRef.current === lease) mutationRef.current = null;
    }
  };

  const visible = state.scope === scopeRef.current;
  return {
    batches: visible ? state.batches : [],
    loading: visible ? state.loading : enabled && !!organizationId,
    error: visible && state.error,
    actionError: visible ? state.actionError : null,
    refresh,
    resumeBatch: (batchId: string) => recover(batchId),
    downloadRecoveredBatch: (batch: MultiPackagePrintResult) => recover(batch.batchId, batch),
  };
}
